import React from 'react';
import { Input } from '@/components/ui/input';
import { Ruler } from 'lucide-react';

export const ProfileStep = ({ formData, setFormData }) => (
  <div className="max-w-sm mx-auto space-y-6">
    <div>
      <label className="text-sm font-medium text-[#2D2D2D] dark:text-[#F0EDE8] mb-2 block flex items-center gap-2">
        <Ruler className="w-4 h-4 text-[#6B9E8A]" /> Measurement Units
      </label>
      <div className="flex gap-3">
        {[
          { id: 'metric', label: 'Metric', desc: 'kg, cm' },
          { id: 'imperial', label: 'Imperial', desc: 'lbs, ft/in' },
        ].map(u => (
          <button
            key={u.id}
            data-testid={`onboarding-unit-${u.id}`}
            onClick={() => setFormData(p => ({ ...p, unit_preference: u.id }))}
            className={`flex-1 p-3 rounded-xl flex flex-col items-center gap-0.5 transition-all border-2 ${
              formData.unit_preference === u.id
                ? 'border-[#1A3A4A] dark:border-[#6B9E8A] bg-[#1A3A4A]/5 dark:bg-[#6B9E8A]/10'
                : 'border-transparent bg-white/80 dark:bg-[#141C22]/80'
            }`}
          >
            <span className={`text-sm font-medium ${formData.unit_preference === u.id ? 'text-[#1A3A4A] dark:text-[#F0EDE8]' : 'text-[#7A7A72]'}`}>{u.label}</span>
            <span className="text-xs text-[#7A7A72]">{u.desc}</span>
          </button>
        ))}
      </div>
    </div>
    {formData.unit_preference === 'imperial' ? (
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-[#2D2D2D] dark:text-[#F0EDE8] mb-1.5 block">Height (ft)</label>
          <Input data-testid="height-ft-input" type="number" value={formData.height_ft} onChange={e => setFormData(p => ({ ...p, height_ft: e.target.value }))} placeholder="5" className="h-12 rounded-xl" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#2D2D2D] dark:text-[#F0EDE8] mb-1.5 block">Height (in)</label>
          <Input data-testid="height-in-input" type="number" value={formData.height_in} onChange={e => setFormData(p => ({ ...p, height_in: e.target.value }))} placeholder="9" className="h-12 rounded-xl" />
        </div>
        <div className="col-span-2">
          <label className="text-sm font-medium text-[#2D2D2D] dark:text-[#F0EDE8] mb-1.5 block">Weight (lbs)</label>
          <Input data-testid="weight-lbs-input" type="number" value={formData.weight_lbs} onChange={e => setFormData(p => ({ ...p, weight_lbs: e.target.value }))} placeholder="165" className="h-12 rounded-xl" />
        </div>
      </div>
    ) : (
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-[#2D2D2D] dark:text-[#F0EDE8] mb-1.5 block">Height (cm)</label>
          <Input data-testid="height-input" type="number" value={formData.height_cm} onChange={e => setFormData(p => ({ ...p, height_cm: e.target.value }))} placeholder="175" className="h-12 rounded-xl" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#2D2D2D] dark:text-[#F0EDE8] mb-1.5 block">Weight (kg)</label>
          <Input data-testid="weight-input" type="number" value={formData.weight_kg} onChange={e => setFormData(p => ({ ...p, weight_kg: e.target.value }))} placeholder="75" className="h-12 rounded-xl" />
        </div>
      </div>
    )}
    <div>
      <label className="text-sm font-medium text-[#2D2D2D] dark:text-[#F0EDE8] mb-1.5 block">Age</label>
      <Input data-testid="age-input" type="number" value={formData.age} onChange={e => setFormData(p => ({ ...p, age: e.target.value }))} placeholder="30" className="h-12 rounded-xl" />
    </div>
    <div>
      <label className="text-sm font-medium text-[#2D2D2D] dark:text-[#F0EDE8] mb-3 block">Biological Sex</label>
      <div className="flex gap-3">
        {['male', 'female'].map(s => (
          <button
            key={s}
            data-testid={`sex-${s}`}
            onClick={() => setFormData(p => ({ ...p, biological_sex: s }))}
            className={`flex-1 h-12 rounded-xl font-medium capitalize transition-all border-2 ${
              formData.biological_sex === s
                ? 'border-[#1A3A4A] dark:border-[#6B9E8A] bg-[#1A3A4A] dark:bg-[#6B9E8A] text-white'
                : 'border-[#1A3A4A]/10 dark:border-white/10 text-[#2D2D2D] dark:text-[#F0EDE8] hover:border-[#1A3A4A]/30'
            }`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>

    {/* Region (optional) */}
    <div>
      <label className="text-sm font-medium text-[#2D2D2D] dark:text-[#F0EDE8] mb-1.5 block">
        Where are you based? <span className="text-xs font-normal text-[#9A8F87]">(optional — helps Nuri suggest meals you'll want to eat)</span>
      </label>
      <select
        data-testid="region-select"
        value={formData.region || ''}
        onChange={e => setFormData(p => ({ ...p, region: e.target.value }))}
        className="w-full h-12 rounded-xl px-3 text-sm border-2 border-[#1A3A4A]/10 dark:border-white/10 bg-white/80 dark:bg-[#141C22]/80 text-[#2D2D2D] dark:text-[#F0EDE8] focus:border-[#1A3A4A] dark:focus:border-[#6B9E8A] outline-none transition-all"
      >
        <option value="">Skip — show me everything</option>
        <option value="new-england">New England (CT, MA, ME, NH, RI, VT)</option>
        <option value="mid-atlantic">Mid-Atlantic (NY, NJ, PA, DE, MD)</option>
        <option value="southeast">Southeast / Deep South</option>
        <option value="gulf-coast">Gulf Coast / Cajun-Creole (LA, MS, AL, TX coast)</option>
        <option value="texas">Texas / Tex-Mex</option>
        <option value="midwest">Midwest (OH, MI, IN, IL, WI, MN, IA, MO)</option>
        <option value="southwest">Southwest (AZ, NM, CO, UT, NV)</option>
        <option value="pacific-northwest">Pacific Northwest (WA, OR)</option>
        <option value="california">California</option>
        <option value="hawaii">Hawaii</option>
        <option value="alaska">Alaska</option>
      </select>
    </div>

    {/* Disliked Foods (optional) */}
    <div>
      <label className="text-sm font-medium text-[#2D2D2D] dark:text-[#F0EDE8] mb-1.5 block">
        Any foods you don't like? <span className="text-xs font-normal text-[#9A8F87]">(optional — we'll avoid them in suggestions)</span>
      </label>
      <input
        data-testid="disliked-foods-onboarding"
        type="text"
        placeholder="e.g., shrimp, cilantro, olives (comma-separated)"
        value={(formData.disliked_foods || []).join(', ')}
        onChange={e => {
          const raw = e.target.value;
          const foods = raw.split(',').map(f => f.trim().toLowerCase()).filter(Boolean);
          setFormData(p => ({ ...p, disliked_foods: foods }));
        }}
        className="w-full h-12 rounded-xl px-3 text-sm border-2 border-[#1A3A4A]/10 dark:border-white/10 bg-white/80 dark:bg-[#141C22]/80 text-[#2D2D2D] dark:text-[#F0EDE8] focus:border-[#1A3A4A] dark:focus:border-[#6B9E8A] outline-none transition-all"
      />
      <div className="flex flex-wrap gap-1.5 mt-2">
        {['Shrimp', 'Cilantro', 'Olives', 'Mushrooms', 'Blue cheese', 'Anchovies', 'Tofu', 'Liver'].map(item => {
          const isActive = (formData.disliked_foods || []).includes(item.toLowerCase());
          return (
            <button
              key={item}
              type="button"
              data-testid={`onboard-dislike-${item.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => {
                setFormData(p => {
                  const current = p.disliked_foods || [];
                  const lower = item.toLowerCase();
                  return { ...p, disliked_foods: isActive ? current.filter(f => f !== lower) : [...current, lower] };
                });
              }}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                isActive
                  ? 'bg-[#C67A5C]/15 text-[#C67A5C] border border-[#C67A5C]/30'
                  : 'bg-[#FAF8F5] dark:bg-[#141C22] text-[#7A7A72] dark:text-[#A1A1AA] border border-[#1A3A4A]/10 dark:border-white/10'
              }`}
            >
              {isActive ? '\u2715 ' : '+ '}{item}
            </button>
          );
        })}
      </div>
    </div>
  </div>
);
